// src/pages/purchase/FinalizeSitePurchase.js
// Direct Site Purchase — Stage 2: Manager/SM/Owner reviews the Site
// Engineer's confirmed quantities, checks the rate, and finalizes.
// This is the ONLY step that writes to site_inventory_summary or
// financial_transactions — same authority boundary as ReceiveMaterial.js.
//
// D-295 (08 Oct 2026):
//   - Rate is prefilled from the PO (editable). The PO's discount, GST and
//     landed rate are shown for reference only.
//   - Lines are matched to the PO with the SAME function the service uses
//     (matchConfirmedToLines), so screen and service always agree.
//   - Short delivery needs a reason (same rule as the normal GRN).
//   - Optional vendor invoice no. / date and actual freight (reference only).
//   - A PO stuck in "finalizing" is shown clearly and cannot be finalized
//     again from here.

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import {
  finalizeSitePurchaseGRN,
  matchConfirmedToLines,
} from "../../services/directSitePurchaseService";

const userData = () => JSON.parse(localStorage.getItem("user") || "{}");
const CAN_FINALIZE = ["manager", "owner", "sm"];

const inr = (n, digits = 2) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: digits });

export default function FinalizeSitePurchase() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const me       = userData();

  const [po,           setPo]           = useState(null);
  const [lines,        setLines]        = useState([]);
  const [itemRates,    setItemRates]    = useState({});
  const [shortReason,  setShortReason]  = useState("");
  const [invoiceNo,    setInvoiceNo]    = useState("");
  const [invoiceDate,  setInvoiceDate]  = useState("");
  const [actualFreight, setActualFreight] = useState("");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [submitting,   setSubmitting]   = useState(false);

  const canFinalize = CAN_FINALIZE.includes((me.role || "").toLowerCase().trim());

  useEffect(() => { if (id) fetchPO(); }, [id]); // eslint-disable-line

  const fetchPO = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, "purchase_orders", id));
      if (!snap.exists()) { setError("Purchase order not found."); return; }

      const poData = { id: snap.id, ...snap.data() };

      if (poData.company_id !== me.company_id) { setError("Access denied."); return; }
      if (poData.purchase_mode !== "direct_site_purchase") {
        setError("This is not a Direct Site Purchase order."); return;
      }
      if (poData.grn_locked) { setError("Already finalized."); return; }
      if (poData.site_confirmation_status === "finalizing") {
        setError(
          `Finalization was started by ${poData.finalizing_by_name || "a manager"} and did not finish. ` +
          `Do not try again — ask admin to check this PO's site stock and vendor bill first.`
        );
        return;
      }
      if (poData.site_confirmation_status !== "site_confirmed") {
        setError("Site Engineer has not confirmed receipt yet."); return;
      }

      // Same matching the service will do — if it refuses, show why now
      // instead of letting the manager fill the form first.
      const matched = matchConfirmedToLines(poData);

      // Prefill each received line's rate with the PO's own rate (editable).
      const prefill = {};
      for (const m of matched) {
        if (m.received_qty > 0 && Number(m.line.rate || 0) > 0) {
          prefill[m.key] = String(m.line.rate);
        }
      }

      setPo(poData);
      setLines(matched);
      setItemRates(prefill);
    } catch (err) {
      console.error("[FinalizeSitePurchase] fetchPO error:", err);
      setError(err.message || "Failed to load purchase order.");
    } finally {
      setLoading(false);
    }
  };

  const rateOf = (key) => Number(itemRates[key] || 0);

  const billTotal = lines.reduce((sum, m) => sum + m.received_qty * rateOf(m.key), 0);

  // Reference only — what the PO's landed cost (after discount, with freight
  // share and GST) would be for the quantity actually received.
  const landedTotal = lines.reduce(
    (sum, m) => sum + m.received_qty * Number(m.line.landed_rate || 0), 0
  );
  const hasLanded = lines.some(m => Number(m.line.landed_rate || 0) > 0);

  const shortLines = lines.filter(m => m.received_qty < Number(m.line.ordered_qty ?? m.line.qty ?? 0));


  const handleFinalize = async () => {
    if (submitting) return;
    if (!canFinalize) {
      alert("Only Manager / SM / Owner can finalize a Direct Site Purchase.");
      return;
    }

    const missingRate = lines.find(m =>
      m.received_qty > 0 && !m.line.is_custom_item && !(rateOf(m.key) > 0)
    );
    if (missingRate) {
      alert(`Rate missing for "${missingRate.line.item_name}". Enter a rate for every received item.`);
      return;
    }
    if (shortLines.length > 0 && !shortReason.trim()) {
      alert("Some items arrived short. Enter a reason — the remaining quantity will be closed.");
      return;
    }
    if (Number(actualFreight || 0) < 0) {
      alert("Actual freight cannot be negative.");
      return;
    }

    const confirmText =
      `Finalize this Direct Site Purchase?\n\n` +
      `Vendor bill: ₹${inr(billTotal)}\n` +
      (shortLines.length > 0 ? `Short items will be closed: ${shortLines.length}\n` : "") +
      `\nThis credits site stock and adds the bill to the vendor's khaata. It cannot be undone.`;
    if (!window.confirm(confirmText)) return;

    setSubmitting(true);
    try {
      const rates = {};
      for (const m of lines) rates[m.key] = rateOf(m.key);

      const result = await finalizeSitePurchaseGRN({
        po_id:               id,
        company_id:          me.company_id,
        rates,
        short_close_reason:  shortReason.trim(),
        vendor_invoice_no:   invoiceNo.trim(),
        vendor_invoice_date: invoiceDate,
        actual_freight:      Number(actualFreight || 0),
        completed_by:        me.uid || me.id || "",
        completed_by_name:   me.name || "",
      });

      alert(
        `Direct Site Purchase finalized.\n` +
        `Vendor bill: ₹${inr(result.grand_total)}\n` +
        `Site stock updated.`
      );
      navigate(`/purchase-orders/${id}`, { replace: true });

    } catch (err) {
      console.error("[FinalizeSitePurchase] error:", err);
      alert(err.message || "Finalization failed.");
      // Only when finalize stopped after the claim: reload so the PO's
      // "finalizing" warning shows instead of the form. A plain validation
      // error leaves the PO untouched, so the manager's entries are kept.
      if (String(err.message || "").includes("stopped part-way")) fetchPO();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 p-6 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-700 font-medium">{error}</p>
      </div>
    </div>
  );

  if (!po) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-slate-800">Finalize Direct Site Purchase</h2>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">Direct Site Purchase</span>
          </div>
          <p className="text-xs text-slate-400">
            PO: {po.po_number || id} • {po.vendor_name || ""} • {po.project_name} — {po.site_name}
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5 text-xs text-green-800">
          <strong>Confirmed by:</strong> {po.site_confirmed_by_name || "Site Engineer"}.
          Check the rate for each item — it is filled from the PO. Finalizing credits site stock
          and adds the bill to the vendor's khaata. Central store stock is not affected.
        </div>

        {!canFinalize && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-xs text-red-700 font-semibold">
            ⛔ Only Manager / SM / Owner can finalize this.
          </div>
        )}

        <div className="space-y-3 mb-5">
          {lines.map(m => {
            const line     = m.line;
            const ordered  = Number(line.ordered_qty ?? line.qty ?? 0);
            const received = m.received_qty;
            const isShort  = received < ordered;
            const isOver   = received > ordered;
            const amount   = received * rateOf(m.key);
            return (
              <div key={m.key} className={`border rounded-2xl p-4 ${
                isShort ? "border-amber-200 bg-amber-50" :
                isOver  ? "border-orange-200 bg-orange-50" :
                          "border-slate-200 bg-white"
              }`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800 text-sm">{line.item_name}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <span className="text-xs text-slate-500">Ordered: {inr(ordered)} {line.unit || "nos"}</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                        Received: {inr(received)} {line.unit || "nos"}
                      </span>
                      {isShort && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                          Short — {inr(ordered - received)} will be closed
                        </span>
                      )}
                      {isOver && (
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full font-medium">
                          Over +{inr(received - ordered)}
                        </span>
                      )}
                      {line.is_custom_item && (
                        <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">⚡ Custom — not added to site stock</span>
                      )}
                    </div>
                    {/* PO reference — never changes the bill */}
                    <p className="text-[11px] text-slate-400 mt-1.5">
                      PO rate ₹{inr(line.base_rate ?? line.rate)}
                      {Number(line.discount_value || 0) > 0 &&
                        ` · discount ${line.discount_type === "flat" ? "₹" : ""}${inr(line.discount_value)}${line.discount_type === "flat" ? "" : "%"}`}
                      {line.gst_percent != null && ` · GST ${inr(line.gst_percent)}%`}
                      {Number(line.landed_rate || 0) > 0 && ` · landed ₹${inr(line.landed_rate)}/unit`}
                    </p>
                  </div>
                  {received > 0 && (
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-400">₹</span>
                        <input
                          type="number" min="0" step="0.01"
                          placeholder={line.is_custom_item ? "Rate (optional)" : "Rate *"}
                          value={itemRates[m.key] ?? ""}
                          onChange={e => setItemRates(prev => ({ ...prev, [m.key]: e.target.value }))}
                          disabled={!canFinalize}
                          className="w-28 border border-slate-200 rounded-xl px-2 py-1.5 text-right text-sm outline-none focus:ring-2 focus:ring-cyan-300 disabled:opacity-50 bg-white"
                        />
                      </div>
                      {amount > 0 && (
                        <span className="text-xs text-slate-500 whitespace-nowrap">= ₹{inr(amount)}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {shortLines.length > 0 && (
          <div className="mb-5">
            <label className="block text-xs font-semibold text-amber-800 mb-1">
              Reason for short delivery * — the remaining quantity will be closed
            </label>
            <textarea
              rows={2}
              value={shortReason}
              onChange={e => setShortReason(e.target.value)}
              disabled={!canFinalize}
              placeholder="e.g. Vendor sent 3 of 5, balance not needed"
              className="w-full border border-amber-300 rounded-xl p-3 text-sm resize-none outline-none focus:ring-2 focus:ring-amber-300 disabled:opacity-50"
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Vendor invoice no. (optional)</label>
            <input
              type="text" value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)}
              disabled={!canFinalize}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-300 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Invoice date (optional)</label>
            <input
              type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
              disabled={!canFinalize}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-300 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Actual freight (optional, PO estimate ₹{inr(po.freight_amount)})
            </label>
            <input
              type="number" min="0" step="0.01" value={actualFreight}
              onChange={e => setActualFreight(e.target.value)}
              disabled={!canFinalize}
              placeholder="0"
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-300 disabled:opacity-50"
            />
          </div>
        </div>

        {hasLanded && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-xs text-slate-600">
            PO landed cost for the received quantity: <strong>₹{inr(landedTotal)}</strong> ·
            this bill: <strong>₹{inr(billTotal)}</strong>
            {Math.abs(landedTotal - billTotal) >= 1 && (
              <> · difference <strong>₹{inr(landedTotal - billTotal)}</strong></>
            )}
            <br />
            The bill is quantity × rate. GST, freight and discount are not added to the bill yet.
          </div>
        )}

        <div className="flex items-center justify-between mb-5 px-1">
          <span className="text-sm text-slate-500">Vendor bill total</span>
          <span className="text-xl font-bold text-slate-800">₹{inr(billTotal)}</span>
        </div>

        <button
          onClick={handleFinalize}
          disabled={submitting || !canFinalize}
          className="w-full py-3 rounded-xl bg-green-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-green-700 transition">
          {submitting ? "Finalizing..." : "✓ Finalize & Update Site Stock"}
        </button>
      </div>
    </div>
  );
}